import { type FC, type ReactNode, useEffect, useMemo } from "react";
import type { EditorProps } from "@hightouch/formkit";
import { Controller, useWatch } from "react-hook-form";
import { Box, Button, Column, Row, Spinner, Text } from "@hightouch/htui";
import { Editor, type Language } from "src/components/editor";
import { useFormkitDataContext } from "src/formkit/components/formkit-context";
import { renderLiquidPreview } from "src/utils/render-liquid-preview";
import type { SharedProps } from ".";

export const FKEditor = (props: EditorProps & SharedProps) => {
  if (props.enablePreview) {
    return (
      <TemplatePreview
        name={props.name}
        language={props.language}
        languageAsLiquidBase={props.languageAsLiquidBase}
      />
    );
  }
  return <EditableEditor {...props} />;
};

const EditableEditor = ({
  name,
  language,
  languageAsLiquidBase,
  placeholder,
  isDisabled,
  error,
}: EditorProps & SharedProps) => {
  const languageLabel = languageAsLiquidBase ? "liquid" : language?.toString();

  return (
    <Controller
      name={name}
      render={({ field }) => (
        <EditorShell label={languageLabel} isInvalid={Boolean(error)}>
          <Editor
            minHeight="200px"
            readOnly={isDisabled}
            value={typeof field.value === "string" ? field.value : ""}
            language={language}
            languageAsLiquidBase={languageAsLiquidBase}
            placeholder={placeholder?.toString()}
            onChange={field.onChange}
          />
        </EditorShell>
      )}
    />
  );
};

type TemplatePreviewProps = {
  name: string;
  language?: Language;
  languageAsLiquidBase?: boolean;
};

const TemplatePreview: FC<TemplatePreviewProps> = ({ name, language }) => {
  const template = (useWatch({ name }) as string | undefined) ?? "";
  const { rows, loadingRows, reloadRows, queryRowsError } =
    useFormkitDataContext();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!rows?.length && !loadingRows) {
      reloadRows();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { output, error } = useMemo(
    () =>
      renderLiquidPreview(
        template,
        {
          row: (rows?.[0] ?? {}) as Record<string, unknown>,
          rows: (rows ?? []) as Record<string, unknown>[],
        },
        language,
      ),
    [template, rows, language],
  );

  return (
    <EditorShell label={language?.toString()}>
      {queryRowsError ? (
        <PreviewMessage variant="danger">{queryRowsError}</PreviewMessage>
      ) : loadingRows ? (
        <Row align="center" gap={2} p={4}>
          <Spinner size="sm" />
          <Text color="text.secondary">Loading sample data from model…</Text>
        </Row>
      ) : !rows?.length ? (
        <Row align="center" justify="space-between" gap={4} p={4}>
          <Text color="text.secondary">No sample data available.</Text>
          <Button size="sm" variant="secondary" onClick={() => reloadRows()}>
            Load preview
          </Button>
        </Row>
      ) : error ? (
        <PreviewMessage variant="danger">{error}</PreviewMessage>
      ) : (
        <Editor
          minHeight="200px"
          readOnly
          value={output ?? ""}
          language={language}
        />
      )}
    </EditorShell>
  );
};

type EditorShellProps = {
  label: ReactNode;
  isInvalid?: boolean;
  children: ReactNode;
};

const EditorShell: FC<EditorShellProps> = ({ label, isInvalid, children }) => (
  <Column
    borderWidth="hairline"
    borderStyle="solid"
    borderColor={isInvalid ? "danger.base" : "base.border"}
    borderRadius="md"
    maxHeight="300px"
    minHeight="200px"
    overflow="hidden"
  >
    <Row
      align="center"
      p={4}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      borderColor="base.border"
      gap={4}
      justify="space-between"
      flexShrink={0}
    >
      <Text fontWeight="medium">{label}</Text>
    </Row>
    {children}
  </Column>
);

const PreviewMessage: FC<{ variant: "danger"; children: ReactNode }> = ({
  children,
}) => (
  <Box
    m={4}
    px={3}
    py={2}
    bg="danger.background"
    borderRadius="md"
    border="1px solid"
    borderColor="danger.border"
  >
    <Box fontFamily="mono" fontSize="sm" color="danger.base">
      {children}
    </Box>
  </Box>
);
